import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, Image as ImageIcon, Edit3, Check, Tag, Search, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name: "", logo_url: "", description: "" };
  const [form, setForm] = useState(blank);
  const [q, setQ] = useState("");
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const { data } = await api.get("/api/admin/brands");
      setBrands(data.brands);
    } catch (e) {
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };
  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/api/admin/brands/${editing}`, form);
        toast.success("Brand updated");
      } else {
        await api.post("/api/admin/brands", form);
        toast.success("Brand added");
      }
      setOpen(false); setEditing(null); setForm(blank);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this brand? Products linked to it will be kept but unlinked.")) return;
    try {
      const { data } = await api.delete(`/api/admin/brands/${id}`);
      toast.success(data.soft_deleted ? `Disabled (still linked to ${data.in_use} products)` : "Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const startEdit = (b) => {
    setEditing(b.id);
    setForm({ name: b.name, logo_url: b.logo_url || "", description: b.description || "" });
    setOpen(true);
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Image files only"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    const fd = new FormData(); fd.append("file", file); fd.append("folder", "brands");
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load brands" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="admin-brands-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Catalog</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Brands ({brands.length})</h1>
          <p className="text-sm text-neutral-600 mt-1">Maintain your brand catalogue. Brands are optional on products.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}
                className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full sm:w-auto"
                data-testid="add-brand-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Brand
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"/>
          <Input placeholder="Search brands" className="pl-10 h-11 rounded-2xl" value={q} onChange={(e) => setQ(e.target.value)} data-testid="brands-search-input"/>
        </div>
      </div>

      {brands.length === 0 ? (
        <div className="rounded-3xl bg-white border border-dashed border-neutral-300" data-testid="empty-brands">
          <EmptyState
            icon={Tag}
            size="lg"
            title="No brands yet"
            description="Add your first brand to start tagging products with it."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {brands.filter((b) => !q.trim() || (b.name || "").toLowerCase().includes(q.toLowerCase()) || (b.slug || "").toLowerCase().includes(q.toLowerCase()) || (b.description || "").toLowerCase().includes(q.toLowerCase())).map((b) => (
            <div key={b.id}
                 className={`bg-white rounded-3xl border ${b.is_active ? "border-neutral-200" : "border-dashed border-neutral-300 opacity-60"} overflow-hidden`}
                 data-testid={`brand-${b.slug}`}>
              <div className="aspect-[16/10] bg-brand-cream relative flex items-center justify-center">
                {b.logo_url ? (
                  <img src={b.logo_url} alt={b.name} className="w-full h-full object-contain p-6"/>
                ) : (
                  <div className="text-neutral-400 text-4xl font-heading font-extrabold">
                    {b.name?.[0]?.toUpperCase()}
                  </div>
                )}
                {!b.is_active && (
                  <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-brand-clay/90 text-[10px] font-bold uppercase tracking-wider text-white">
                    disabled
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-bold truncate">{b.name}</div>
                    <div className="text-xs text-neutral-500 font-mono mt-0.5">{b.slug}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-extrabold text-brand-sky">{b.product_count || 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">products</div>
                  </div>
                </div>
                {b.description && (
                  <p className="text-xs text-neutral-600 mt-2 line-clamp-2">{b.description}</p>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => startEdit(b)} className="text-sm text-brand-sky font-bold inline-flex items-center gap-1" data-testid={`edit-brand-${b.slug}`}>
                    <Edit3 className="w-3.5 h-3.5"/> Edit
                  </button>
                  <button onClick={() => del(b.id)} className="text-sm text-brand-clay font-bold inline-flex items-center gap-1 ml-auto" data-testid={`delete-brand-${b.slug}`}>
                    <Trash2 className="w-3.5 h-3.5"/> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blank); } }}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit brand" : "New brand"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Amul" className="h-12 rounded-2xl mt-1" data-testid="brand-name"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="A short tagline shown in product detail" className="rounded-2xl mt-1" rows={2}
                        data-testid="brand-description"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Logo</label>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-16 h-16 rounded-2xl bg-brand-cream border border-neutral-200 flex items-center justify-center overflow-hidden shrink-0">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="" className="w-full h-full object-contain p-2" data-testid="brand-logo-preview"/>
                  ) : <ImageIcon className="w-6 h-6 text-neutral-400"/>}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="brand-file-input"/>
                <Button onClick={() => fileInputRef.current?.click()} disabled={busy} variant="outline"
                        className="h-12 rounded-2xl border-2 border-brand-ink font-bold flex-1"
                        data-testid="brand-upload-btn">
                  <Upload className="w-4 h-4 mr-2"/> {form.logo_url ? "Replace" : "Upload"} logo
                </Button>
              </div>
            </div>
            <Button onClick={save} disabled={busy} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-brand-btn">
              <Check className="w-4 h-4 mr-2"/> {editing ? "Save changes" : "Create brand"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
